import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Plus, Check, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const NEW = '__new__';
const norm = (s) => (s || '').trim().toLowerCase();

export const useSubjects = () => {
    const [subjects, setSubjects] = useState([]);
    const load = useCallback(() => axios.get(`${API_URL}/api/subjects`, { withCredentials: true }).then(r => setSubjects(r.data || [])).catch(() => {}), []);
    useEffect(() => { load(); }, [load]);
    return [subjects, load];
};

export const subjectLabel = (s) => s ? `${s.name_en || ''}${s.name_ar ? ` · ${s.name_ar}` : ''}` : '';
export const findSubject = (subjects, nameEn, nameAr) => subjects.find(s => (nameEn && norm(s.name_en) === norm(nameEn)) || (nameAr && s.name_ar && norm(s.name_ar) === norm(nameAr)));
// Books whose subject matches the subject name (English or Arabic).
export const booksForSubject = (books, subject) => {
    if (!subject) return [];
    const names = [subject.name_en, subject.name_ar].filter(Boolean).map(norm);
    return books.filter(b => { const bs = norm(b.subject); return bs && names.some(n => bs === n || bs.includes(n) || n.includes(bs)); });
};

// Dropdown of catalog subjects with an inline "add new subject" option. onChange receives the subject doc (or null).
export const SubjectSelect = ({ value, onChange, subjects, onCreated, testId, placeholder = 'Select subject' }) => {
    const [adding, setAdding] = useState(false);
    const [en, setEn] = useState('');
    const [ar, setAr] = useState('');
    const [busy, setBusy] = useState(false);
    const save = async () => {
        if (!en.trim() && !ar.trim()) { toast.error('Enter the subject name'); return; }
        setBusy(true);
        try { const r = await axios.post(`${API_URL}/api/subjects`, { name_en: en, name_ar: ar }, { withCredentials: true }); toast.success('Subject added'); await onCreated?.(); onChange(r.data); setAdding(false); setEn(''); setAr(''); }
        catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
    };
    if (adding) return (
        <div className="flex items-center gap-1" data-testid={`${testId}-new`}>
            <Input value={en} onChange={e => setEn(e.target.value)} placeholder="English name" className="h-9" data-testid={`${testId}-new-en`} />
            <Input value={ar} onChange={e => setAr(e.target.value)} placeholder="الاسم بالعربية" dir="rtl" className="h-9" data-testid={`${testId}-new-ar`} />
            <Button type="button" size="sm" className="bg-[#064E3B] hover:bg-[#047857] h-9" disabled={busy} onClick={save} data-testid={`${testId}-new-save`}><Check className="h-4 w-4" /></Button>
            <Button type="button" size="sm" variant="ghost" className="h-9" onClick={() => setAdding(false)}><X className="h-4 w-4" /></Button>
        </div>
    );
    return (
        <Select value={value || 'none'} onValueChange={(v) => { if (v === NEW) setAdding(true); else onChange(v === 'none' ? null : subjects.find(s => s.id === v)); }}>
            <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
            <SelectContent className="max-h-72">
                <SelectItem value="none">{placeholder}</SelectItem>
                {subjects.map(s => <SelectItem key={s.id} value={s.id}>{subjectLabel(s)}</SelectItem>)}
                <SelectItem value={NEW} className="text-[#064E3B] font-medium"><span className="inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" />Add new subject…</span></SelectItem>
            </SelectContent>
        </Select>
    );
};
